import { useState, useEffect } from 'react';

import useAuthStore from '@entities/auth/model/useAuthStore';
import {
  useGetEmployeesByTeamQuery,
  useGetEmployeeByNameQuery,
  useGetEmployeesByCompanyQuery,
} from '@features/employee/hooks/index';
import { useSaveSignLineMutation, useSignLineModify } from '@features/signLine/hooks';
import useGetTeamQuery from '@features/team/hooks';
import Alert from '@shared/components/atoms/Alert';
import { useAlert } from '@shared/hooks';

import OrganizationTree from '@features/document-write/components/OrganizationTree/OrganizationTree';

import styles from './ApprovalLineSettingModal.module.scss';

// 역할별 인원 제한 상수
const ROLE_LIMITS = {
  결재: 4,
  검토: 4,
  합의: 1,
};

const ROLE_MAP = {
  결재: 'sign',
  합의: 'agreement',
  검토: 'check',
};

/**
 * 결재라인 설정 모달 (추가/수정)
 * - ApprovalLineModal과 동일한 고품질 UI 적용
 */
function ApprovalLineSettingModal({ isOpen, onClose, onSave, editingLine = null }) {
  const [selectedTeamCode, setSelectedTeamCode] = useState(null);
  const [isCompanySelected, setIsCompanySelected] = useState(false); // 회사(최상위 조직) 선택 상태
  const [searchName, setSearchName] = useState('');
  const [searchTrigger, setSearchTrigger] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [lineName, setLineName] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [checkedEmployees, setCheckedEmployees] = useState(new Set());

  const { alertState, showAlert, closeAlert } = useAlert();
  const { enumber, companyCode } = useAuthStore();

  const MAX_SELECTED_EMPLOYEES = 9;

  // 모달 오픈 시 배경(body) 스크롤 방지
  useEffect(
    function () {
      if (!isOpen) {
        return undefined;
      }

      const currentCount = Number(document.body.dataset.modalLockCount || '0');
      const nextCount = currentCount + 1;
      document.body.dataset.modalLockCount = String(nextCount);

      if (currentCount === 0) {
        document.body.dataset.modalLockOverflow = document.body.style.overflow || '';
        document.body.dataset.modalLockPaddingRight = document.body.style.paddingRight || '';

        const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        if (scrollBarWidth > 0) {
          document.body.style.paddingRight = `${scrollBarWidth}px`;
        }
      }

      return function () {
        const count = Number(document.body.dataset.modalLockCount || '0');
        const restoredCount = Math.max(0, count - 1);
        document.body.dataset.modalLockCount = String(restoredCount);

        if (restoredCount === 0) {
          document.body.style.overflow = document.body.dataset.modalLockOverflow || '';
          document.body.style.paddingRight = document.body.dataset.modalLockPaddingRight || '';
          delete document.body.dataset.modalLockOverflow;
          delete document.body.dataset.modalLockPaddingRight;
        }
      };
    },
    [isOpen],
  );

  // 저장
  const saveSignLineMutation = useSaveSignLineMutation({
    onSuccess: () => {
      if (onSave) onSave({ name: lineName.trim() });
      closeAlert();
      onClose();
    },
    onError: () => {
      showAlert('결재라인 저장 중 오류가 발생했습니다.');
    },
  });

  // 수정
  const signLineModifyMutation = useSignLineModify({
    onSuccess: () => {
      if (onSave) onSave({ name: lineName.trim() });
      closeAlert();
      onClose();
    },
    onError: () => {
      showAlert('결재라인 수정 중 오류가 발생했습니다.');
    },
  });

  // 수정 payload 구성 (사용자 지정 순서 그대로 반영, sign_1 기안자는 제외하여 유지)
  function buildUpdateSetValues(selectedEmployees, lineName) {
    const parts = [];
    let signSlot = 2; // sign_2 ~ sign_5
    let checkSlot = 1; // check_1 ~ check_4
    let agreementDone = false;

    // 사용자 목록 순서대로 전역 order(2, 3, 4, ...) 부여 후 역할별 슬롯에 매핑
    selectedEmployees.forEach(function (emp, index) {
      const globalOrder = index + 2;
      if (emp.role === '결재' && signSlot <= 5) {
        parts.push(`sign_${signSlot}=${emp.enumber}`);
        parts.push(`sign_${signSlot}_order=${globalOrder}`);
        signSlot += 1;
      } else if (emp.role === '검토' && checkSlot <= 4) {
        parts.push(`check_${checkSlot}=${emp.enumber}`);
        parts.push(`check_${checkSlot}_order=${globalOrder}`);
        checkSlot += 1;
      } else if (emp.role === '합의' && !agreementDone) {
        parts.push(`agreement=${emp.enumber}`);
        parts.push(`agreement_order=${globalOrder}`);
        agreementDone = true;
      }
    });

    // 사용하지 않은 슬롯은 NULL로 초기화 (sign_1은 수정하지 않음)
    for (let i = 2; i <= 5; i++) {
      if (i >= signSlot) {
        parts.push(`sign_${i}=NULL`);
        parts.push(`sign_${i}_order=NULL`);
      }
    }
    for (let i = 1; i <= 4; i++) {
      if (i >= checkSlot) {
        parts.push(`check_${i}=NULL`);
        parts.push(`check_${i}_order=NULL`);
      }
    }
    if (!agreementDone) {
      parts.push(`agreement=NULL`);
      parts.push(`agreement_order=NULL`);
    }

    parts.push(`title='${lineName.replace(/'/g, "''")}'`);
    return parts.join(', ');
  }

  // 초기화
  useEffect(() => {
    if (isOpen) {
      closeAlert();
      if (editingLine) {
        setLineName(editingLine.name || '');
        setSelectedEmployees(editingLine.members || []);
      } else {
        setLineName('');
        setSelectedEmployees([]);
      }
      setCheckedEmployees(new Set());
      setSearchName('');
      setSearchTrigger('');
      setSelectedTeamCode(null);
      setIsCompanySelected(false);
    }
  }, [isOpen, editingLine]);

  const teamQuery = useGetTeamQuery(companyCode, {
    enabled: isOpen && !!companyCode,
  });
  const teamList = teamQuery.data?.resultData?.teamList || [];

  const employeeQuery = useGetEmployeesByTeamQuery(selectedTeamCode, {
    enabled: isOpen && !!selectedTeamCode && !isCompanySelected,
  });
  const teamEmployees = employeeQuery.data?.resultData?.employeeList || [];

  // 회사(최상위 조직) 선택 시 대표이사 조회
  const companyEmployeeQuery = useGetEmployeesByCompanyQuery(companyCode, {
    enabled: isOpen && isCompanySelected && !!companyCode,
  });
  const companyEmployeesRaw = companyEmployeeQuery.data?.resultData?.employeeList || [];
  // 대표이사만 필터링 (직책이 "대표이사"인 직원만)
  const companyEmployees = companyEmployeesRaw.filter(function (emp) {
    return emp.position === '대표이사' || emp.position === '대표';
  });

  // 현재 표시할 직원 목록 (회사 선택 시 대표이사, 팀 선택 시 팀원)
  const employees = isCompanySelected ? companyEmployees : teamEmployees;

  const employeeNameQuery = useGetEmployeeByNameQuery(searchTrigger, {
    enabled: isOpen && searchTrigger.length >= 1,
  });
  const searchResults = employeeNameQuery.data?.resultData?.employeeList || [];

  if (!isOpen) return null;

  function handleTeamSelect(teamCode) {
    setSelectedTeamCode(teamCode);
    setIsCompanySelected(false); // 팀 선택 시 회사 선택 해제
    setSearchName('');
    setSearchTrigger('');
  }

  // 회사(최상위 조직) 선택 핸들러
  function handleCompanySelect() {
    setSelectedTeamCode(null); // 팀 선택 해제
    setIsCompanySelected(true);
    setSearchName('');
    setSearchTrigger('');
  }

  function checkDuplicateEmployee(employee) {
    return selectedEmployees.some((emp) => emp.enumber === employee.enumber);
  }

  function handleAddEmployee(employee, role = '선택') {
    if (selectedEmployees.length >= MAX_SELECTED_EMPLOYEES) {
      showAlert('결재라인은 최대 9명까지 구성 가능합니다.');
      return;
    }
    if (checkDuplicateEmployee(employee)) {
      showAlert('이미 결재라인에 존재하는 사원입니다.');
      return;
    }

    const newEmployee = {
      enumber: employee.enumber,
      name: employee.name,
      position: employee.position || '',
      team: employee.team || '',
      role,
    };
    setSelectedEmployees((prev) => {
      const alreadyExists = prev.some((emp) => emp.enumber === employee.enumber);
      if (alreadyExists) {
        return prev;
      }
      return [...prev, newEmployee];
    });
  }

  function handleCheckboxToggle(employee) {
    const isChecked = checkedEmployees.has(employee.enumber);
    const isInSelected = selectedEmployees.some((emp) => emp.enumber === employee.enumber);

    if (isChecked) {
      setCheckedEmployees((prev) => {
        const next = new Set(prev);
        next.delete(employee.enumber);
        return next;
      });
      setSelectedEmployees((prev) => prev.filter((emp) => emp.enumber !== employee.enumber));
    } else {
      if (isInSelected) {
        setCheckedEmployees((prev) => {
          const next = new Set(prev);
          next.add(employee.enumber);
          return next;
        });
        return;
      }

      if (selectedEmployees.length >= MAX_SELECTED_EMPLOYEES) {
        showAlert('최대 9명까지 선택 가능합니다.');
        return;
      }

      if (checkDuplicateEmployee(employee)) {
        showAlert('이미 선택된 사원입니다.');
        return;
      }

      setCheckedEmployees((prev) => {
        const next = new Set(prev);
        next.add(employee.enumber);
        return next;
      });
      setSelectedEmployees((prev) => {
        const alreadyExists = prev.some((emp) => emp.enumber === employee.enumber);
        if (alreadyExists) {
          return prev;
        }
        return [
          ...prev,
          {
            enumber: employee.enumber,
            name: employee.name,
            position: employee.position || '',
            team: employee.team || '',
            role: '선택',
          },
        ];
      });
    }
  }

  const showSearchResults = searchTrigger.length >= 1;
  const displayEmployees = (showSearchResults ? searchResults : employees).filter(
    (emp) => emp.enumber !== enumber, // 본인 제외 (이미 기안자로 포함)
  );

  function handleEmployeeDragStart(e, employee) {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify(employee));
    e.target.style.opacity = '0.5';
  }

  function handleEmployeeDragEnd(e) {
    e.target.style.opacity = '1';
  }

  function handleDropToSelectedSection(e) {
    e.preventDefault();
    const data = e.dataTransfer.getData('application/json');
    if (!data) return;

    try {
      const employee = JSON.parse(data);
      handleAddEmployee(employee, '선택');
    } catch (err) {
      console.error('Failed parsing drop data:', err);
    }
  }

  function handleRemoveEmployee(index) {
    const removed = selectedEmployees[index];
    setSelectedEmployees(selectedEmployees.filter((_, i) => i !== index));
    setCheckedEmployees((prev) => {
      const next = new Set(prev);
      next.delete(removed.enumber);
      return next;
    });
  }

  function handleRoleChange(index, newRole) {
    const updated = [...selectedEmployees];
    const current = updated[index];

    if (newRole === '선택') {
      updated[index].role = newRole;
      setSelectedEmployees(updated);
      return;
    }

    const newRoleCount = updated.filter((emp) => emp.role === newRole && emp !== current).length;
    const roleLimit = ROLE_LIMITS[newRole]; // 결재·검토 각 4명, 합의 1명

    if (newRoleCount >= roleLimit) {
      showAlert(`${newRole}는 ${roleLimit}명까지만 선택 가능합니다.`);
      return;
    }

    updated[index].role = newRole;
    setSelectedEmployees(updated);
  }

  function handleDragStart(e, index) {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  }

  function handleDragEnd(e) {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }

  function handleDrop(e, dropIndex) {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const updated = [...selectedEmployees];
    const draggedItem = updated[draggedIndex];

    updated.splice(draggedIndex, 1);
    updated.splice(dropIndex, 0, draggedItem);

    setSelectedEmployees(updated);
    setDraggedIndex(null);
  }

  function handleSearch() {
    setSearchTrigger(searchName);
    setSelectedTeamCode(null);
  }

  // 추가 시: 사용자 지정 순서(selectedEmployees 배열 순서) 그대로 전역 order로 저장
  function buildSignLinePayload(selectedEmployees, lineName, userEnumber, companyCode) {
    const columns = [];
    const values = [];
    let signSlot = 2; // sign_2 ~ sign_5 (sign_1은 기안자)
    let checkSlot = 1; // check_1 ~ check_4
    let agreementDone = false;

    // 기안자(sign_1) 먼저
    columns.push('sign_1', 'sign_1_order');
    values.push(userEnumber, 1);

    // selectedEmployees 순서대로 전역 order 2, 3, 4, ... 부여 후 역할별 슬롯에 매핑
    selectedEmployees.forEach(function (emp, index) {
      const globalOrder = index + 2;
      if (emp.role === '결재' && signSlot <= 5) {
        columns.push(`sign_${signSlot}`, `sign_${signSlot}_order`);
        values.push(emp.enumber, globalOrder);
        signSlot += 1;
      } else if (emp.role === '검토' && checkSlot <= 4) {
        columns.push(`check_${checkSlot}`, `check_${checkSlot}_order`);
        values.push(emp.enumber, globalOrder);
        checkSlot += 1;
      } else if (emp.role === '합의' && !agreementDone) {
        columns.push('agreement', 'agreement_order');
        values.push(emp.enumber, globalOrder);
        agreementDone = true;
      }
    });

    return {
      enumber: userEnumber,
      company: companyCode,
      title: lineName,
      column: columns.join(','),
      values: values.join(','),
    };
  }

  function handleSave() {
    if (!lineName.trim()) {
      showAlert('결재라인명을 입력해주세요.');
      return;
    }
    if (selectedEmployees.length === 0) {
      showAlert('최소 1명 이상의 구성원을 추가해주세요.');
      return;
    }
    if (selectedEmployees.some((e) => e.role === '선택')) {
      showAlert('역할이 지정되지 않은 직원이 있습니다.');
      return;
    }

    const payload = buildSignLinePayload(selectedEmployees, lineName.trim(), enumber, companyCode);

    if (editingLine) {
      const setValue = buildUpdateSetValues(selectedEmployees, lineName.trim());
      signLineModifyMutation.mutate({ idx: editingLine.id, setValue });
    } else {
      saveSignLineMutation.mutate(payload);
    }
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} role="dialog" aria-modal="true">
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{editingLine ? '결재라인 수정' : '결재라인 추가'}</h2>
          <button type="button" className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        {/* 결재라인명 입력 */}
        <div className={styles.lineNameSection}>
          <label className={styles.label}>결재라인명</label>
          <input
            type="text"
            className={styles.lineNameInput}
            placeholder="결재라인명을 입력하세요"
            value={lineName}
            onChange={(e) => setLineName(e.target.value)}
          />
        </div>

        <div className={styles.modalBody}>
          <div className={styles.contentLayout}>
            {/* 왼쪽: 조직도 트리 */}
            <div className={`${styles.columnContainer} ${styles.treeSection}`}>
              <div className={styles.columnHeader}>
                <div className={styles.columnTitle}>조직도</div>
              </div>
              <div className={styles.treeContent}>
                <OrganizationTree
                  companyName={teamList?.[0]?.company || '링크아이'}
                  teamList={teamList}
                  selectedTeamCode={selectedTeamCode}
                  onSelectTeam={handleTeamSelect}
                  onSelectCompany={handleCompanySelect}
                  isCompanySelected={isCompanySelected}
                  height={510}
                />
              </div>
            </div>

            {/* 가운데: 임직원 목록 */}
            <div className={`${styles.columnContainer} ${styles.employeeSection}`}>
              <div className={styles.columnHeader}>
                <div className={styles.columnTitle}>임직원</div>
                <div className={styles.searchBlock}>
                  <div className={styles.searchRow}>
                    <div className={styles.searchLabelCell}>이름</div>
                    <div className={styles.searchControlCell}>
                      <input
                        type="text"
                        className={styles.searchInput}
                        style={{ width: '130px' }}
                        value={searchName}
                        onChange={(e) => setSearchName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      />
                      <button type="button" className={styles.smallButton} onClick={handleSearch}>
                        검색
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* 직원 목록 테이블 */}
              <div className={styles.employeeTableBlock}>
                <div className={styles.employeeTableSection}>
                  <table className={styles.employeeTable}>
                    <thead>
                      <tr>
                        <th className={styles.thCenter}>
                          <input
                            type="checkbox"
                            checked={
                              displayEmployees.length > 0 &&
                              displayEmployees.every((emp) => checkedEmployees.has(emp.enumber))
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                const newChecked = new Set(checkedEmployees);
                                const newEmployees = [...selectedEmployees];
                                const existingEnumbers = new Set(
                                  newEmployees.map((e) => e.enumber),
                                );
                                displayEmployees.forEach((emp) => {
                                  const alreadyInList = existingEnumbers.has(emp.enumber);
                                  if (
                                    !alreadyInList &&
                                    newEmployees.length < MAX_SELECTED_EMPLOYEES
                                  ) {
                                    existingEnumbers.add(emp.enumber);
                                    newChecked.add(emp.enumber);
                                    newEmployees.push({
                                      enumber: emp.enumber,
                                      name: emp.name,
                                      position: emp.position || '',
                                      team: emp.team || '',
                                      role: '선택',
                                    });
                                  }
                                });
                                setCheckedEmployees(newChecked);
                                setSelectedEmployees(newEmployees);
                              } else {
                                const currentEnumbers = displayEmployees.map((e) => e.enumber);
                                setCheckedEmployees((prev) => {
                                  const next = new Set(prev);
                                  currentEnumbers.forEach((n) => next.delete(n));
                                  return next;
                                });
                                setSelectedEmployees((prev) =>
                                  prev.filter((emp) => !currentEnumbers.includes(emp.enumber)),
                                );
                              }
                            }}
                          />
                        </th>
                        <th className={styles.thCenter}>부서</th>
                        <th className={styles.thCenter}>직급</th>
                        <th className={styles.thCenter}>이름</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayEmployees.length > 0 ? (
                        displayEmployees.map((employee) => (
                          <tr
                            key={employee.enumber}
                            className={styles.employeeRow}
                            draggable
                            onDragStart={(e) => handleEmployeeDragStart(e, employee)}
                            onDragEnd={handleEmployeeDragEnd}
                            onClick={(e) => {
                              if (e.target.type === 'checkbox') {
                                return;
                              }
                              handleCheckboxToggle(employee);
                            }}
                          >
                            <td className={styles.tdCenter}>
                              <input
                                type="checkbox"
                                checked={checkedEmployees.has(employee.enumber)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleCheckboxToggle(employee);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              />
                            </td>
                            <td className={styles.tdCenter}>{employee.team || '-'}</td>
                            <td className={styles.tdCenter}>{employee.position || '-'}</td>
                            <td className={styles.tdCenter}>{employee.name}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className={styles.emptyCell}>
                            {showSearchResults
                              ? '검색 결과가 없습니다.'
                              : '부서를 선택하거나 검색하세요.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* 오른쪽: 선택된 구성원 카드 목록 */}
            <div
              className={`${styles.columnContainer} ${styles.selectedSection}`}
              onDragOver={handleDragOver}
              onDrop={handleDropToSelectedSection}
            >
              <div className={styles.columnHeader}>
                <div className={styles.columnTitle}>결재라인 구성원</div>
              </div>
              <div className={styles.selectedList}>
                {selectedEmployees.length > 0 ? (
                  selectedEmployees.map((employee, index) => (
                    <div
                      key={`${employee.enumber}-${index}`}
                      className={styles.approverCard}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={handleDragOver}
                      onDragEnd={handleDragEnd}
                      onDrop={(e) => handleDrop(e, index)}
                    >
                      <span className={styles.cardIndex}>{index + 1}</span>
                      <select
                        className={styles.roleDropdown}
                        value={employee.role}
                        onChange={(e) => handleRoleChange(index, e.target.value)}
                      >
                        <option value="선택">선택</option>
                        <option value="결재">결재</option>
                        <option value="검토">검토</option>
                        <option value="합의">합의</option>
                      </select>
                      <span className={styles.approverName}>{employee.name}</span>
                      <span className={styles.approverTeam}>{employee.team || '-'}</span>
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => handleRemoveEmployee(index)}
                        aria-label="제거"
                      >
                        ✕
                      </button>
                    </div>
                  ))
                ) : (
                  <div className={styles.emptyCell}>선택된 직원이 없습니다.</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            className={`${styles.footerButton} ${styles.btnCancel}`}
            onClick={onClose}
          >
            취소
          </button>
          <button
            type="button"
            className={`${styles.footerButton} ${styles.btnSave}`}
            onClick={handleSave}
          >
            저장
          </button>
        </div>
      </div>

      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
    </div>
  );
}

export default ApprovalLineSettingModal;
